import { useCallback, useEffect, useState } from "react";
import { Typography, Form, Flex, Spin } from "antd";
import {
  WhiteBoxBlock,
  CustomButton,
  FileInput,
  DefaultInput,
  RadioButtonGroup,
  NumberInput,
  filesToFormDataSingle,
} from "ui";
import styles from "./web-settings.module.css";
import { FILE_TYPE_ENUM, NOTIFICATION_TYPE_ENUM } from "interfaces";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import { _isRequired, _isPhoneNumber, _isEmail, parseErrorMessage, _isNotLongerThan, _greaterThan } from "utils";
import { TitleProps } from "antd/es/typography/Title";
import { WebSettingsEntity } from "interfaces";
import { updateWebSettings, uploadLogo, uploadFavicon } from "services";
import { useNotificationContext } from "providers";
import { useStores } from "stores";
import { observer } from "mobx-react";
import { THEME_ENUM } from "stores/WebSettings";
import { requestWithNotify } from "utils";
import { UploadFile } from "antd/lib/upload";
import { S3FileType } from "interfaces";

export const SubTitle = ({ children, ...props }: TitleProps) => {
  return (
    <Typography.Title level={4} className="margin-bottom-none margin-top-none" {...props}>
      {children}
    </Typography.Title>
  );
};

export const WebSettings = observer(() => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<WebSettingsEntity>();
  const { openNotification } = useNotificationContext();
  const { webSettings } = useStores();

  const onFinish = useCallback(async () => {
    const values = form.getFieldsValue();

    setLoading(true);

    await requestWithNotify({
      fetchFn: () => updateWebSettings(values),
      openNotification: openNotification,
      successMsg: "Данные успешно обновлены",
    });

    await requestWithNotify({
      fetchFn: webSettings.settings.fetch,
      openNotification: openNotification,
    });

    setLoading(false);
  }, [form, openNotification]);

  const handleUploadLogo = useCallback(async (files: UploadFile[], type: S3FileType) => {
    const formData = filesToFormDataSingle(files, type);
    const response = await uploadLogo(formData);

    await requestWithNotify({
      fetchFn: webSettings.logo.fetch,
      openNotification: openNotification,
      successMsg: "Логотип успешно загружен",
    });

    return response;
  }, []);

  const handleUploadFavicon = useCallback(async (files: UploadFile[], type: S3FileType) => {
    const formData = filesToFormDataSingle(files, type);
    const response = await uploadFavicon(formData);

    await requestWithNotify({
      fetchFn: webSettings.favicon.fetch,
      openNotification: openNotification,
      successMsg: "Favicon успешно загружен",
    });

    return response;
  }, []);

  if (webSettings.settings.statuses.loading) {
    return <Spin spinning={true} />;
  }

  return (
    <Flex vertical gap="large">
      <Typography.Title>Кастомизация интерфейса</Typography.Title>

      <Form
        form={form}
        name="updateWebSettings"
        initialValues={webSettings.settings.data}
        onFinish={onFinish}
        layout="vertical"
      >
        <Flex vertical gap="large" className={styles.interfaceCustomizationBlock}>
          <WhiteBoxBlock>
            <Flex vertical className={styles.topFormBlock}>
              <Flex vertical gap="middle">
                <Flex vertical gap="small">
                  <SubTitle>Тема</SubTitle>
                  <Typography.Text>
                    Выберите тему шапки — светлую или темную, для лучшего восприятия логотипа вашей компании
                  </Typography.Text>
                </Flex>
                <Form.Item name="theme" noStyle>
                  <RadioButtonGroup
                    options={[
                      { label: "Темная", value: THEME_ENUM.DARK },
                      { label: "Светлая", value: THEME_ENUM.LIGHT },
                    ]}
                  />
                </Form.Item>
              </Flex>

              <FileInput
                blockTitle={<SubTitle>Логотип</SubTitle>}
                blockDescription="Загрузите логотип компании в формате .png или .svg"
                fileType={FILE_TYPE_ENUM.LOGO}
                handleUpload={handleUploadLogo}
                className="full-width"
                accept={[".png", ".svg"]}
              />

              <FileInput
                blockTitle={<SubTitle>Favicon</SubTitle>}
                blockDescription="Загрузите favicon в формате .ico, .png или .svg"
                fileType={FILE_TYPE_ENUM.FAVICON}
                handleUpload={handleUploadFavicon}
                className="full-width"
                accept={[".ico", ".png", ".svg"]}
              />

              <Flex vertical gap="middle">
                <SubTitle>Заголовок приложения</SubTitle>
                <Form.Item
                  name="title"
                  rules={[_isRequired("Заголовок приложения обязателен для ввода")]}
                  validateDebounce={FORM_VALIDATION_DEBOUNCE}
                >
                  <DefaultInput placeholder="Текст заголовка" maxLength={40} />
                </Form.Item>
              </Flex>
            </Flex>
          </WhiteBoxBlock>

          <WhiteBoxBlock>
            <Flex vertical className={styles.bottomFormBlock}>
              <SubTitle className="margin-bottom-md margin-top-none">Контактные данные</SubTitle>

              <Flex vertical gap="small">
                <Form.Item
                  name={["phoneNumbers", 0]}
                  rules={[_greaterThan(0)]}
                  validateDebounce={FORM_VALIDATION_DEBOUNCE}
                >
                  <DefaultInput placeholder="Телефон тех. поддержки" type="text" />
                </Form.Item>

                <Form.Item
                  name="email"
                  rules={[_isRequired("Email обязателен для ввода"), _isEmail()]}
                  validateDebounce={FORM_VALIDATION_DEBOUNCE}
                >
                  <DefaultInput placeholder="Email" maxLength={40} />
                </Form.Item>

                <Form.Item
                  rules={[_isRequired("Copyright обязателен для ввода")]}
                  name="copyrightText"
                  validateDebounce={FORM_VALIDATION_DEBOUNCE}
                >
                  <DefaultInput placeholder="Copyright" maxLength={256} />
                </Form.Item>
              </Flex>

              <Form.Item className="margin-bottom-none">
                <CustomButton htmlType="submit" loading={loading} tooltip="Сохранить указанные настройки">
                  Сохранить изменения
                </CustomButton>
              </Form.Item>
            </Flex>
          </WhiteBoxBlock>
        </Flex>
      </Form>
    </Flex>
  );
});
